import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { ExistingResult } from '../components/organisms/DocumentationList/components/ActionForms/CloseProcessingAction/types'
import { CloseProcessingFormInput } from '../components/organisms/DocumentationList/components/ActionForms/CloseProcessingAction/validationSchema.zod'
import { ContactFormInput } from '../components/organisms/DocumentationList/components/ActionForms/ContactedAction/validationSchema.zod'
import { FirstContactFormInput } from '../components/organisms/DocumentationList/components/ActionForms/FirstContactAction/validationSchema.zod'
import { ForwardActionFormInput } from '../components/organisms/DocumentationList/components/ActionForms/ForwardAction/validationSchema.zod'
import { NoticeActionFormData } from '../components/organisms/DocumentationList/components/ActionForms/NoticeAction/validationSchema.zod'
import { RequestedInfoActionFormData } from '../components/organisms/DocumentationList/components/ActionForms/RequestedInfoAction/validationSchema.zod'
import { ActionContactTypeEnum } from '../graphql/generated'
import { ForwardAttachment } from '../utils/forwardAttachments'

type NoticeActionContext = {
  caseId: string
  processingId?: string
  initialData?: Partial<NoticeActionFormData>
  actionId?: string // only for edit mode
}

type RequestedInfoActionContext = {
  caseId: string
  processingId?: string
  initialData?: Partial<RequestedInfoActionFormData>
  actionId?: string // only for edit mode
}

type ContactFormContext = {
  caseId: string
  processingId?: string
  initialData?: Partial<ContactFormInput>
  actionId?: string // only for edit mode
  defaultContactType?: ActionContactTypeEnum
}

type ForwardActionContext = {
  caseId: string
  processingId?: string
  initialData?: Partial<ForwardActionFormInput>
  actionId?: string // only for edit mode
}

type FirstCallActionContext = {
  processingId?: string
  initialData?: Partial<FirstContactFormInput>
  actionId?: string // only for edit mode
}

type SendEmailActionContext = {
  caseId: string
  processingId?: string
  draftId?: string
  initialToAddress?: string
  /** Vorbelegter Betreff (ohne [Aktenzeichen]-Präfix), z. B. „Re: …" / „Fwd: …" */
  initialSubject?: string
  /** Antworten: die neue Mail hängt sich an den Thread der Original-Mail */
  replyToEmailId?: string
  /** Weiterleiten: zitierter Block der Original-Mail, wird unter die Signatur gesetzt */
  forwardQuoteHtml?: string
  /** Forward: attachments of the original email, preselected and removable */
  forwardAttachments?: ForwardAttachment[]
}

type CloseProcessingActionContext = {
  processingId?: string
  initialData?: Partial<CloseProcessingFormInput>
  actionId?: string // only for edit mode
  result?: ExistingResult // only for edit mode: persisted result for read-only display
}

// Discriminated Union for Modal States
type ModalState =
  | { type: 'closed' }
  | {
      type: 'NoticeAction'
      mode: 'create' | 'edit'
      context: NoticeActionContext
    }
  | {
      type: 'RequestedInfoAction'
      mode: 'create' | 'edit'
      context: RequestedInfoActionContext
    }
  | {
      type: 'ContactedAction'
      mode: 'create' | 'edit'
      context: ContactFormContext
    }
  | {
      type: 'ForwardAction'
      mode: 'create' | 'edit'
      context: ForwardActionContext
    }
  | {
      type: 'FirstCallAction'
      mode: 'create' | 'edit'
      context: FirstCallActionContext
    }
  | {
      type: 'SendEmailAction'
      mode: 'create'
      context: SendEmailActionContext
    }
  | {
      type: 'CloseProcessingAction'
      mode: 'create' | 'edit'
      context: CloseProcessingActionContext
    }

// Store Interface
type ModalStore = {
  modal: ModalState

  // Actions für NoticeAction
  openCreateNoticeAction: (context: Omit<NoticeActionContext, 'actionId'>) => void
  openEditNoticeAction: (context: NoticeActionContext) => void

  // Actions für RequestedInfoAction
  openCreateRequestedInfoAction: (context: Omit<RequestedInfoActionContext, 'actionId'>) => void
  openEditRequestedInfoAction: (context: RequestedInfoActionContext) => void

  // Actions für ContactedAction (ContactForm)
  openCreateContactAction: (context: Omit<ContactFormContext, 'actionId'>) => void
  openEditContactAction: (context: ContactFormContext) => void

  // Actions für ForwardAction
  openCreateForwardAction: (context: Omit<ForwardActionContext, 'actionId'>) => void
  openEditForwardAction: (context: ForwardActionContext) => void

  // Actions für FirstCallAction
  openCreateFirstCallAction: (context: Omit<FirstCallActionContext, 'actionId'>) => void
  openEditFirstCallAction: (context: FirstCallActionContext) => void

  // Actions für SendEmailAction
  openSendEmailAction: (context: SendEmailActionContext) => void
  // Actions für CloseProcessingAction
  openCreateCloseProcessingAction: (context: Omit<CloseProcessingActionContext, 'actionId'>) => void
  openEditCloseProcessingAction: (context: CloseProcessingActionContext) => void

  // Actions für MessageToCustomerAction
  // openCreateMessageToCustomerAction: (
  //   context: Omit<MessageToCustomerActionContext, 'actionId'>
  // ) => void
  // openEditMessageToCustomerAction: (context: MessageToCustomerActionContext) => void

  // Global Actions
  closeModal: () => void

  // Getters (as functions)
  isOpen: () => boolean
  currentModalType: () => string
  isCreateMode: () => boolean
  isEditMode: () => boolean
}

// Zustand Store
export const useModalStore = create<ModalStore>()(
  devtools(
    (set, get) => ({
      modal: { type: 'closed' },

      // MessageAction Actions
      // openCreateMessageAction: context =>
      //   set({
      //     modal: {
      //       type: 'MessageAction',
      //       mode: 'create',
      //       context
      //     }
      //   }),

      // openEditMessageAction: context =>
      //   set({
      //     modal: {
      //       type: 'MessageAction',
      //       mode: 'edit',
      //       context
      //     }
      //   }),

      // NoticeAction Actions
      openCreateNoticeAction: context =>
        set({
          modal: {
            type: 'NoticeAction',
            mode: 'create',
            context
          }
        }),

      openEditNoticeAction: context =>
        set({
          modal: {
            type: 'NoticeAction',
            mode: 'edit',
            context
          }
        }),

      // RequestedInfoAction Actions
      openCreateRequestedInfoAction: context =>
        set({
          modal: {
            type: 'RequestedInfoAction',
            mode: 'create',
            context
          }
        }),

      openEditRequestedInfoAction: context =>
        set({
          modal: {
            type: 'RequestedInfoAction',
            mode: 'edit',
            context
          }
        }),

      // ContactedAction Actions (ContactForm)
      openCreateContactAction: context =>
        set({
          modal: {
            type: 'ContactedAction',
            mode: 'create',
            context
          }
        }),

      openEditContactAction: context =>
        set({
          modal: {
            type: 'ContactedAction',
            mode: 'edit',
            context
          }
        }),

      // ForwardAction Actions
      openCreateForwardAction: context =>
        set({
          modal: {
            type: 'ForwardAction',
            mode: 'create',
            context
          }
        }),

      openEditForwardAction: context =>
        set({
          modal: {
            type: 'ForwardAction',
            mode: 'edit',
            context
          }
        }),

      // FirstCallAction Actions
      openCreateFirstCallAction: context =>
        set({
          modal: {
            type: 'FirstCallAction',
            mode: 'create',
            context
          }
        }),

      openEditFirstCallAction: context =>
        set({
          modal: {
            type: 'FirstCallAction',
            mode: 'edit',
            context
          }
        }),

      // SendEmailAction
      openSendEmailAction: context =>
        set({
          modal: {
            type: 'SendEmailAction',
            mode: 'create',
            context
          }
        }),
      // CloseProcessingAction Actions
      openCreateCloseProcessingAction: context =>
        set({
          modal: {
            type: 'CloseProcessingAction',
            mode: 'create',
            context
          }
        }),

      openEditCloseProcessingAction: context =>
        set({
          modal: {
            type: 'CloseProcessingAction',
            mode: 'edit',
            context
          }
        }),

      // MessageToCustomerAction Actions
      // openCreateMessageToCustomerAction: context =>
      //   set({
      //     modal: {
      //       type: 'CustomerMessageAction',
      //       mode: 'create',
      //       context
      //     }
      //   }),

      // openEditMessageToCustomerAction: context =>
      //   set({
      //     modal: {
      //       type: 'CustomerMessageAction',
      //       mode: 'edit',
      //       context
      //     }
      //   }),

      // Global Actions
      closeModal: () => set({ modal: { type: 'closed' } }),

      // Computed Getters (as functions)
      isOpen: () => {
        return get().modal.type !== 'closed'
      },

      currentModalType: () => {
        return get().modal.type
      },

      isCreateMode: () => {
        const modal = get().modal
        return modal.type !== 'closed' && modal.mode === 'create'
      },

      isEditMode: () => {
        const modal = get().modal
        return modal.type !== 'closed' && modal.mode === 'edit'
      }
    }),
    {
      name: 'modal-store' // für DevTools
    }
  )
)

// Export Types für externe Verwendung
export type {
  CloseProcessingActionContext,
  ContactFormContext,
  FirstCallActionContext,
  ForwardActionContext,
  NoticeActionContext,
  RequestedInfoActionContext,
  SendEmailActionContext
}
